/**
 * 设定引用模态框组件
 *
 * 提供工作区设定引用功能，支持：
 * - 从后端获取工作区设定列表
 * - 搜索过滤设定
 * - 选择设定并插入 @{setting_name} 引用标记
 * - 空列表时显示创建设定链接
 * - 键盘快捷键 Ctrl+Shift+@
 * - 淡黄色主题配色
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SettingService } from '../../services/setting';

/**
 * 设定信息接口
 */
interface SettingInfo {
  id: number;
  name: string;
  level: string;
  scope: string | null;
  setting_type: string;
  content: string;
}

/**
 * SettingsReferenceModal组件属性接口
 */
interface SettingsReferenceModalProps {
  /** 是否显示模态框 */
  isOpen: boolean;
  /** 关闭模态框回调 */
  onClose: () => void;
  /** 选择设定后的回调，返回引用标记 */
  onSelectSetting: (reference: string) => void;
  /** JWT认证令牌 */
  token: string;
  /** 工作区ID */
  workspaceId: number;
}

/**
 * 关闭图标
 */
const XIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

/**
 * 搜索图标
 */
const SearchIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

/**
 * 加载图标
 */
const SpinnerIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

/**
 * SettingsReferenceModal组件
 */
export const SettingsReferenceModal: React.FC<SettingsReferenceModalProps> = React.memo(({
  isOpen,
  onClose,
  onSelectSetting,
  token,
  workspaceId,
}) => {
  // ===== 状态管理 =====
  const [settings, setSettings] = useState<SettingInfo[]>([]);
  const [filteredSettings, setFilteredSettings] = useState<SettingInfo[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * 获取工作区设定列表
   */
  useEffect(() => {
    const fetchSettings = async () => {
      if (!isOpen || !token || !workspaceId) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const settingService = new SettingService(undefined, token, workspaceId);
        const settingsData = await settingService.listSettings(workspaceId);

        if (Array.isArray(settingsData)) {
          setSettings(settingsData);
          setFilteredSettings(settingsData);
        } else {
          setSettings([]);
          setFilteredSettings([]);
        }
      } catch (err) {
        console.error('Failed to fetch settings:', err);
        setError('获取设定列表失败');
        setSettings([]);
        setFilteredSettings([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [isOpen, token, workspaceId]);

  /**
   * 搜索过滤设定
   */
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredSettings(settings);
      setFocusedIndex(null);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = settings.filter((setting) =>
      setting.name.toLowerCase().includes(query) ||
      setting.level.toLowerCase().includes(query) ||
      setting.setting_type.toLowerCase().includes(query)
    );
    setFilteredSettings(filtered);
    setFocusedIndex(null);
  }, [searchQuery, settings]);

  /**
   * 模态框打开时聚焦搜索框
   */
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  /**
   * 键盘快捷键 Ctrl+Shift+@
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === '@') {
        e.preventDefault();
        if (!isOpen) {
          // 需要父组件打开模态框
          // 这里只是示例，实际实现需要父组件控制
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  /**
   * 处理搜索输入变化
   */
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  /**
   * 选择设定
   */
  const handleSelectSetting = useCallback((setting: SettingInfo) => {
    const reference = `@{${setting.name}}`;
    onSelectSetting(reference);
    handleClose();
  }, [onSelectSetting]);

  /**
   * 关闭模态框
   */
  const handleClose = useCallback(() => {
    setSearchQuery('');
    setFilteredSettings(settings);
    setError(null);
    onClose();
  }, [onClose, settings]);

  /**
   * 处理键盘导航
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();

      if (filteredSettings.length === 0) return;

      setFocusedIndex((prev) => {
        if (prev === null) {
          return e.key === 'ArrowDown' ? 0 : filteredSettings.length - 1;
        }

        if (e.key === 'ArrowDown') {
          return (prev + 1) % filteredSettings.length;
        } else {
          return (prev - 1 + filteredSettings.length) % filteredSettings.length;
        }
      });
      return;
    }

    if (e.key === 'Enter' && focusedIndex !== null && focusedIndex >= 0 && focusedIndex < filteredSettings.length) {
      e.preventDefault();
      handleSelectSetting(filteredSettings[focusedIndex]);
    }
  }, [filteredSettings, focusedIndex, handleSelectSetting, handleClose]);

  /**
   * 点击背景关闭
   */
  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  /**
   * 确保聚焦的选项可见
   */
  useEffect(() => {
    if (focusedIndex !== null && listRef.current) {
      const focusedElement = listRef.current.children[focusedIndex] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex]);

  // ===== 渲染函数 =====

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-modal-title"
    >
      <div className="relative bg-white rounded-lg shadow-sm max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col border-2 border-gray-200">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-lg">
          <h2 id="settings-modal-title" className="text-lg font-semibold text-gray-900">
            引用工作区设定
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 rounded"
            aria-label="关闭"
          >
            <XIcon />
          </button>
        </div>

        {/* 搜索框 */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="搜索设定名称或分类..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent text-sm"
              aria-label="搜索设定"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <SearchIcon className="w-5 h-5" />
            </div>
          </div>
          {/* 快捷键提示 */}
          <div className="mt-2 text-xs text-gray-600">
            提示：使用键盘快捷键 Ctrl+Shift+@ 快速打开
          </div>
        </div>

        {/* 设定列表 */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <SpinnerIcon className="w-6 h-6 text-gray-700" />
              <span className="ml-2 text-sm text-gray-700">加载中...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-sm text-red-600 mb-4">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setIsLoading(true);
                  // 重新触发fetch
                }}
                className="px-4 py-2 text-sm bg-ink-800 hover:bg-ink-700 text-white rounded-lg shadow-sm hover:shadow-sm active:scale-[0.98] transition-all duration-150"
              >
                重试
              </button>
            </div>
          ) : filteredSettings.length === 0 ? (
            <div className="text-center py-8">
              {searchQuery ? (
                <p className="text-sm text-gray-600">未找到匹配的设定</p>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">暂无工作区设定</p>
                  <a
                    href={`/workspace/${workspaceId}/settings`}
                    className="inline-block px-4 py-2 text-sm bg-ink-800 hover:bg-ink-700 text-white rounded-lg shadow-sm hover:shadow-sm active:scale-[0.98] transition-all duration-150"
                  >
                    创建设定
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div ref={listRef} className="space-y-2" role="listbox" aria-label="设定列表">
              {filteredSettings.map((setting, index) => {
                const isFocused = index === focusedIndex;

                return (
                  <button
                    key={setting.id}
                    onClick={() => handleSelectSetting(setting)}
                    onMouseEnter={() => setFocusedIndex(index)}
                    onMouseLeave={() => setFocusedIndex(null)}
                    className={`
                      w-full text-left px-4 py-3 rounded-lg transition-colors
                      ${isFocused
                        ? 'bg-gray-50 outline-none ring-2 ring-gray-500'
                        : 'hover:bg-white'
                      }
                    `}
                    role="option"
                    aria-selected={false}
                    tabIndex={-1}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {setting.name}
                        </div>
                        <div className="text-xs text-gray-600 mt-1 flex gap-2">
                          <span>{setting.level}</span>
                          <span>•</span>
                          <span>{setting.setting_type}</span>
                        </div>
                        {setting.content && (
                          <div className="text-xs text-gray-700 mt-1 truncate">
                            {setting.content}
                          </div>
                        )}
                      </div>
                      <div className="ml-3 flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 底部 */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-600">
              找到 {filteredSettings.length} 个设定
            </div>
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors"
            >
              取消
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

SettingsReferenceModal.displayName = 'SettingsReferenceModal';

export default SettingsReferenceModal;
